// 줄 마진·판매가·포함↔별도 단가 변환 — 순수 함수 · 반올림은 마지막 한 번이고 정수 나머지로 해 부동소수가 .5 를 뒤집지 않는다.

import { checked } from "../money/checked";
import type { VatMode } from "../money/vat";

// 마진을 백만분율 정수로 다룬다 — 사람이 친 마진(25.5%)은 여기서 정확히 떨어진다.
const PPM = 1_000_000;

// 판매 단가에 곱하는 부가세 배수의 분자(÷10) — 포함 11/10 · 별도 10/10.
const vatNumerator = (mode: VatMode): number => (mode === "INCL" ? 11 : 10);

// 음이 아닌 안전정수 num ÷ den 을 반올림(.5 는 올림) — 나머지로 판정해 정확하다.
const divRound = (num: number, den: number): number => {
  const r = num % den;
  const q = (num - r) / den;
  return 2 * r >= den ? q + 1 : q;
};

// 원가 대비 마진 = 판매 공급단가 ÷ 원가 − 1 (포함이면 판매단가 ÷ 1.1) · 원가 0/없음이면 null.
export const marginOf = (
  saleUnit: number,
  cost: number | null | undefined,
  vatMode: VatMode
): number | null => {
  if (cost === null || cost === undefined || cost <= 0) return null;
  const n = vatNumerator(vatMode);
  return (saleUnit * 10 - n * cost) / (n * cost);
};

// 판매가를 끊는 단위 — 마진으로 역산한 판매가는 100원 단위로 반올림한다(50원은 올림 · #198).
export const SALE_ROUND_UNIT = 100;

// 원가 × (1 + 마진) × 배수 의 정확한 값 = won + rem/den (won 은 내림 · den 0 은 0 아래로 내려간 경우) — 반올림 전 한 벌.
const exactSale = (cost: number, margin: number, vatMode: VatMode): { won: number; rem: number; den: number } => {
  const base = checked(cost * vatNumerator(vatMode), "판매가");
  const scaled = margin * PPM;
  const ppm = Math.round(scaled);
  // 백만분율로 안 떨어지는 마진(marginOf 가 낸 값)은 곱한 값을 부동소수로 든다.
  if (Math.abs(scaled - ppm) > 1e-6) {
    const raw = (base * (1 + margin)) / 10;
    if (raw <= 0) return { won: 0, rem: 0, den: 0 };
    const den = 1_000_000;
    const won = Math.floor(raw);
    return { won: checked(won, "판매가"), rem: Math.round((raw - won) * den), den };
  }
  const factor = PPM + ppm;
  if (factor <= 0) return { won: 0, rem: 0, den: 0 };
  const den = 10 * PPM;
  // base × factor 가 2^53 을 넘지 않게 base 를 den 으로 쪼개 곱한다 — 몫(원)과 나머지(÷den)를 따로 든다.
  const r = base % den;
  const q = (base - r) / den;
  const part = checked(r * factor, "판매가");
  const rem = part % den;
  return { won: checked(checked(q * factor, "판매가") + (part - rem) / den, "판매가"), rem, den };
};

// 마진 → 판매 단가를 원 단위로(.5 올림) — #198 이전에 저장된 줄의 계산값을 알아보는 데만 쓴다.
export const saleFromMarginWon = (cost: number, margin: number, vatMode: VatMode): number => {
  const { won, rem, den } = exactSale(cost, margin, vatMode);
  return den === 0 ? 0 : checked(won + (2 * rem >= den ? 1 : 0), "판매가");
};

// 마진 → 판매 단가 = round₁₀₀(원가 × (1 + 마진) × (포함 1.1 / 별도 1)) · 100원 미만은 원 단위(0원이 되지 않게) · 화면·저장이 이 한 벌을 쓴다.
export const saleFromMargin = (
  cost: number,
  margin: number,
  vatMode: VatMode
): number => {
  const { won, rem, den } = exactSale(cost, margin, vatMode);
  if (den === 0) return 0;
  if (won < SALE_ROUND_UNIT) return saleFromMarginWon(cost, margin, vatMode);
  // 100원 단위 반올림도 나머지로 판정한다 — 원 단위로 먼저 반올림하면 149.5 → 150 → 200 처럼 두 번 올라간다.
  const below = won % SALE_ROUND_UNIT;
  const up = 2 * (below * den + rem) >= SALE_ROUND_UNIT * den;
  return checked(won - below + (up ? SALE_ROUND_UNIT : 0), "판매가");
};

// 판매가가 이 마진의 계산값인가 — 지금 계산값(100원) 또는 #198 이전 원 단위 계산값과 같으면 파생 · 넘치면 아니다.
export const isSaleFromMargin = (sale: number, cost: number, margin: number, vatMode: VatMode): boolean => {
  try {
    return sale === saleFromMargin(cost, margin, vatMode) || sale === saleFromMarginWon(cost, margin, vatMode);
  } catch {
    return false;
  }
};

// 단가의 부가세 방식 변환 — 포함→별도 round(÷1.1) · 별도→포함 round(×1.1) · 왕복하면 1원 차이 가능.
export const convertUnitPrice = (
  price: number,
  from: VatMode,
  to: VatMode
): number => {
  if (from === to) return price;
  checked(price, "단가");
  return from === "INCL"
    ? divRound(checked(price * 10, "단가"), 11)
    : divRound(checked(price * 11, "단가"), 10);
};
